import { getSupabaseClient } from "./index";

export type InboxConversation = {
  id: string;
  tenant_id: string;
  status: string;
  customer_phone: string;
  customer_name: string | null;
  order_id: string | null;
  attributes: Record<string, unknown>;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  last_message_preview?: string | null;
  last_message_role?: string | null;
};

export type InboxMessage = {
  id: string;
  conversation_id: string;
  direction: "inbound" | "outbound";
  role: "customer" | "assistant" | "staff" | "system";
  content: string;
  message_type: string;
  wa_message_id: string | null;
  status: string;
  created_at: string;
  metadata?: Record<string, unknown>;
};

export async function listInboxConversations(input: {
  tenantId: string;
  search?: string;
  limit?: number;
}): Promise<InboxConversation[]> {
  const supabase = getSupabaseClient();
  const limit = Math.min(Math.max(input.limit ?? 40, 1), 100);

  let q = supabase
    .from("conversations")
    .select(
      "id, tenant_id, status, customer_phone, customer_name, order_id, attributes, last_message_at, created_at, updated_at"
    )
    .eq("tenant_id", input.tenantId)
    .order("last_message_at", { ascending: false, nullsFirst: false })
    .limit(limit);

  const term = input.search?.trim();
  if (term) {
    const pattern = `%${term}%`;
    q = q.or(`customer_phone.ilike.${pattern},customer_name.ilike.${pattern}`);
  }

  const { data, error } = await q;
  if (error) throw error;
  const rows = (data ?? []) as InboxConversation[];
  if (!rows.length) return [];

  const ids = rows.map((r) => r.id);
  const { data: msgs, error: msgErr } = await supabase
    .from("messages")
    .select("conversation_id, content, role, created_at")
    .in("conversation_id", ids)
    .order("created_at", { ascending: false });
  if (msgErr) throw msgErr;

  const latest = new Map<string, { content: string; role: string }>();
  for (const m of msgs ?? []) {
    const cid = String((m as { conversation_id: string }).conversation_id);
    if (latest.has(cid)) continue;
    latest.set(cid, {
      content: String((m as { content: string }).content ?? ""),
      role: String((m as { role: string }).role ?? ""),
    });
  }

  return rows.map((r) => ({
    ...r,
    last_message_preview: latest.get(r.id)?.content?.slice(0, 140) ?? null,
    last_message_role: latest.get(r.id)?.role ?? null,
  }));
}

export async function getInboxConversation(
  conversationId: string
): Promise<InboxConversation | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("conversations")
    .select(
      "id, tenant_id, status, customer_phone, customer_name, order_id, attributes, last_message_at, created_at, updated_at"
    )
    .eq("id", conversationId)
    .maybeSingle();
  if (error) throw error;
  return (data as InboxConversation | null) ?? null;
}

export async function listInboxMessages(
  conversationId: string,
  limit = 200
): Promise<InboxMessage[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("messages")
    .select(
      "id, conversation_id, direction, role, content, message_type, wa_message_id, status, created_at, metadata"
    )
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true })
    .limit(Math.min(Math.max(limit, 1), 500));
  if (error) throw error;
  return (data ?? []) as InboxMessage[];
}

/** True while OELOX AI is processing a reply for this chat (Inbox typing bubble). */
export async function isInboxConversationTyping(
  conversationId: string
): Promise<boolean> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("conversation_ai_jobs")
    .select("id")
    .eq("conversation_id", conversationId)
    .eq("status", "processing")
    .limit(1);
  if (error) throw error;
  return Boolean(data?.length);
}

export type InboxHandoffMode = "manual" | "ai";

/** Staff take over a chat (pause AI) or resume AI auto-replies for this conversation. */
export async function setInboxConversationHandoff(
  conversationId: string,
  mode: InboxHandoffMode
): Promise<InboxConversation> {
  const supabase = getSupabaseClient();
  const { data: current, error: readErr } = await supabase
    .from("conversations")
    .select("id, attributes, status")
    .eq("id", conversationId)
    .maybeSingle();
  if (readErr) throw readErr;
  if (!current) throw new Error("Conversation not found");

  const attrs = {
    ...((current.attributes as Record<string, unknown>) || {}),
  };

  if (mode === "manual") {
    attrs.handoffMode = "manual";
    attrs.aiOrderPhase = "handed_off";
    attrs.needsStaff = true;
    attrs.staffTakeoverAt = new Date().toISOString();
  } else {
    attrs.handoffMode = "ai";
    attrs.aiOrderPhase = "building";
    attrs.needsStaff = false;
    delete attrs.aiFailure;
    delete attrs.staffTakeoverAt;
  }

  const { data, error } = await supabase
    .from("conversations")
    .update({
      status: mode === "manual" ? "handed_off" : "open",
      attributes: attrs,
      updated_at: new Date().toISOString(),
    })
    .eq("id", conversationId)
    .select(
      "id, tenant_id, status, customer_phone, customer_name, order_id, attributes, last_message_at, created_at, updated_at"
    )
    .single();
  if (error) throw error;

  // Staff-visible system note
  const { data: conv } = await supabase
    .from("conversations")
    .select("tenant_id")
    .eq("id", conversationId)
    .maybeSingle();
  if (conv?.tenant_id) {
    await supabase.from("messages").insert({
      tenant_id: conv.tenant_id,
      conversation_id: conversationId,
      direction: "outbound",
      role: "system",
      content:
        mode === "manual"
          ? "Staff took over — AI paused for this chat. Reply from WhatsApp on your phone."
          : "AI resumed for this chat.",
      message_type: "system",
      status: "complete",
      metadata: { kind: mode === "manual" ? "staff_takeover" : "ai_resumed" },
    });
  }

  return data as InboxConversation;
}

export function conversationNeedsStaff(c: {
  status?: string;
  attributes?: Record<string, unknown> | null;
}): boolean {
  if (c.status === "handed_off") return true;
  const attrs = c.attributes || {};
  return attrs.needsStaff === true || attrs.handoffMode === "manual";
}

export function conversationAiFailureReason(c: {
  attributes?: Record<string, unknown> | null;
}): string | null {
  const failure = c.attributes?.aiFailure;
  if (!failure || typeof failure !== "object") return null;
  const kind = String((failure as { kind?: string }).kind || "unknown");
  const reason = String((failure as { reason?: string }).reason || "").slice(0, 120);
  return reason ? `${kind}: ${reason}` : kind;
}
